/**
 * FriendAvatar Component
 * Shared avatar component for friend cards with priority hierarchy:
 * 1. avatarId (AvatarDisplay) > 2. fallbackUrl (Image) > 3. initials (Text)
 */

import React from 'react';
import { View, Text } from 'react-native';
import { Image } from 'expo-image';
import type { ImageSourcePropType } from 'react-native';
import type { ViewStyle, ImageStyle, StyleProp } from 'react-native';
import type { DisplayAvatarId, AvatarMood, AvatarHomeAway } from '../../../src/avatars/types/avatar.types';
import type { EquippedMap } from '../../avatars/LayeredAvatarLab/characterCustomizer.types';
import { AvatarDisplay } from '../../avatars/AvatarDisplay/AvatarDisplay';
import {
  isAvatarUrl,
  normalizeAvatarImageUri,
  getInitials,
} from '../../../src/create/helpers/createUi.helper';
import { friendAvatarStyles as styles, getFriendAvatarSizeStyles } from './FriendAvatar.styles';
import { webImageAltAvatarLabel, webImageAltGroupCover } from '../../../src/shared/helpers/imageWebAlt.helper';

export interface FriendAvatarProps {
  /** Local image source (e.g. group default image). Rendered first when provided. */
  imageSource?: ImageSourcePropType;
  /** Avatar identifier from user profile (user-selectable or display-only e.g. monkey) */
  avatarId?: DisplayAvatarId | null;
  /** Custom layered look; when non-null it overrides avatarId and renders the layered idle composite. */
  avatarConfig?: EquippedMap | null;
  /** Fallback URL for avatar image */
  fallbackUrl?: string;
  /** Size in pixels (width and height) */
  size: number;
  /** Name for generating initials fallback */
  nameForInitials?: string;
  /** Optional custom styling */
  style?: ViewStyle;
  /** Mood for avatar expression (idle, happy, sad) when using avatarId */
  mood?: AvatarMood;
  /** When false, do not crop avatar to a circle. */
  avatarRounded?: boolean;
  /** Home/away for success screen: home = challenger (us), away = opponent (them). When set, overrides mood. */
  variant?: AvatarHomeAway;
  /** Test ID for testing */
  testID?: string;
  /** Force the 150x150 CDN variant when using avatarId. */
  small?: boolean;
}

/**
 * FriendAvatar Component
 * Renders avatar with priority: avatarId > fallbackUrl > initials
 */
export const FriendAvatar: React.FC<FriendAvatarProps> = ({
  imageSource,
  avatarId,
  avatarConfig,
  fallbackUrl,
  size,
  nameForInitials,
  style,
  mood,
  avatarRounded = true,
  variant,
  testID,
  small,
}) => {
  const sizeStyles = getFriendAvatarSizeStyles(size, avatarRounded);

  // Priority 0: Use imageSource when provided (e.g. group default image)
  if (imageSource) {
    return (
      <Image
        source={imageSource}
        style={[styles.fallbackImage, sizeStyles.fallbackImage, style] as StyleProp<ImageStyle>}
        contentFit="cover"
        cachePolicy="memory-disk"
        testID={testID}
        alt={webImageAltGroupCover(nameForInitials ?? 'Group')}
      />
    );
  }

  // Priority 1: Use AvatarDisplay if a custom look OR an avatarId is provided.
  // (A custom user may have a stale/empty avatarId, so avatarConfig alone must qualify.)
  if (avatarConfig || avatarId) {
    return (
      <View style={[sizeStyles.container, style]} testID={testID}>
        <AvatarDisplay
          avatarId={avatarId ?? undefined}
          avatarConfig={avatarConfig}
          mood={mood}
          variant={variant}
          size={size}
          small={small}
          rounded={avatarRounded}
          imageAlt={webImageAltAvatarLabel(nameForInitials)}
        />
      </View>
    );
  }

  // Priority 2: Use Image if fallbackUrl is a valid URL
  if (fallbackUrl && isAvatarUrl(fallbackUrl)) {
    return (
      <Image
        source={{ uri: normalizeAvatarImageUri(fallbackUrl) }}
        style={[styles.fallbackImage, sizeStyles.fallbackImage, style] as StyleProp<ImageStyle>}
        contentFit="cover"
        cachePolicy="memory-disk"
        testID={testID}
        alt={webImageAltAvatarLabel(nameForInitials)}
      />
    );
  }

  // Priority 3: Default placeholder with initials or generic icon
  const initials = nameForInitials ? getInitials(nameForInitials) : '👤';

  return (
    <View style={[styles.placeholderContainer, sizeStyles.container, style]} testID={testID}>
      <Text style={[styles.placeholderText, sizeStyles.placeholderText]}>{initials}</Text>
    </View>
  );
};

